import * as React from "react";
import { CopyIcon, Pressable } from "../../../ui";
import { useCopyToClipboard } from "./useCopyToClipboard";
import { NotRecorded } from "../../NotRecorded";
import { BoxLabel } from "./BoxLabel";

const boxStyle: React.CSSProperties = {
  justifyContent: "space-between",
  minHeight: 48,
  border: "1px solid #44576e",
  borderRadius: 4,
  padding: "6px 6px 6px 8px",
};

/**
 * A bordered button on the page's black: a small label over the value, a copy icon at its end. The whole box copies,
 * so the tap target is the box, not just the icon. Nothing recorded: the same box, a red -- and nothing to copy.
 */
export const IdentifierBox: React.FC<{
  label: string;
  value?: string | null;
  /** Shown after the value and a center dot, smaller: the plate's state. */
  suffix?: string | null;
  monospace?: boolean;
}> = ({ label, value, suffix, monospace }) => {
  const copy = useCopyToClipboard();
  if (!value) {
    return (
      <div className="flex items-center min-w-0" style={boxStyle}>
        <span className="flex flex-col min-w-0">
          <BoxLabel>{label}</BoxLabel>
          <span className="text-[13px] font-semibold">
            <NotRecorded />
          </span>
        </span>
      </div>
    );
  }
  return (
    // Inline box style: the button's own reset (no border, no padding) would win over utility classes.
    <Pressable
      // Names the value too: the label replaces the box text for screen readers.
      aria-label={`Copy ${label} ${value}`}
      onClick={() => copy(value, label)}
      className="flex items-center gap-1 min-w-0 text-left"
      style={boxStyle}
    >
      <span className="flex flex-col min-w-0">
        <BoxLabel>{label}</BoxLabel>
        <span className="flex items-baseline gap-1 min-w-0">
          <span
            className={`font-semibold text-[#e2e8f0] break-all ${
              monospace ? "font-mono text-[12.5px]" : "text-[13px]"
            }`}
          >
            {value}
          </span>
          {suffix && <span className="text-[11px] font-semibold text-[#94a3b8] shrink-0">· {suffix}</span>}
        </span>
      </span>
      <CopyIcon aria-hidden style={{ fontSize: 15, color: "#94a3b8", flexShrink: 0 }} />
    </Pressable>
  );
};
